import { Stack } from "@mui/material";
import { Title } from "@src/appV2/redesign/system/ui-components";

import { Button } from "../../components/Button";
import type { Offer, OpenShift, OpenShiftWorkplace } from "../../Shift/Open/types";
import { FallbackShiftsList } from "./FallbackShiftsList";
import { WorkerReferralCta } from "./WorkerReferralCta";
import { WorkplaceReferralCta } from "./WorkplaceReferralCta";

interface FallbackShiftsFooterProps {
  shifts: OpenShift[];
  totalShiftsCount: number;
  workplacesMap: Map<string, OpenShiftWorkplace>;
  onClickOpenShift: ({
    shift,
    workplace,
    offer,
  }: {
    shift: OpenShift;
    workplace: OpenShiftWorkplace;
    offer?: Offer;
  }) => void;
  onClearFilters: () => void;
}

/**
 * Footer component that displays fallback shifts when filters are applied
 * but return fewer than 15 shifts. `shifts` is deduplicated against what is
 * already visible in the main list; `totalShiftsCount` is the raw unfiltered
 * total used in the "Reset filters to see all N shifts" label.
 */
export function FallbackShiftsFooter(props: FallbackShiftsFooterProps) {
  const { shifts, totalShiftsCount, workplacesMap, onClickOpenShift, onClearFilters } = props;

  return (
    <Stack sx={{ flex: "1", pt: 8, pb: 12 }} justifyContent="center" spacing={5}>
      <Title semibold variant="h4" component="h3" sx={{ textAlign: "center" }}>
        We&apos;re working on getting more shifts in your area!
      </Title>

      <Button
        variant="contained"
        size="medium"
        sx={{ width: "fit-content", alignSelf: "center" }}
        onClick={onClearFilters}
      >
        Reset filters to see all {totalShiftsCount} shifts
      </Button>
      <Stack spacing={3}>
        <WorkerReferralCta />
        <WorkplaceReferralCta />
      </Stack>
      <FallbackShiftsList
        shifts={shifts}
        totalShiftsCount={totalShiftsCount}
        workplacesMap={workplacesMap}
        onClickOpenShift={onClickOpenShift}
      />
    </Stack>
  );
}
